import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Save } from 'lucide-react';
import type { LabTest } from '@/services/labExtractionService';

interface ManualLabEntryFormProps {
  patientId: string;
  onSave?: (tests: LabTest[], metadata: { provider: string; collectionDate: string }) => Promise<void>;
  onCancel?: () => void;
}

const emptyTest: LabTest = {
  test_name: '',
  value: null,
  unit: '',
  reference_range: null,
  status: null,
  performed_at: null,
  lab_name: null,
};

const ManualLabEntryForm: React.FC<ManualLabEntryFormProps> = ({
  patientId,
  onSave,
  onCancel,
}) => {
  const { toast } = useToast();
  const [tests, setTests] = useState<LabTest[]>([{ ...emptyTest }]);
  const [provider, setProvider] = useState('');
  const [collectionDate, setCollectionDate] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAddTest = () => {
    setTests([...tests, { ...emptyTest }]);
  };

  const handleRemoveTest = (index: number) => {
    if (tests.length === 1) {
      toast({
        title: 'Cannot remove',
        description: 'At least one test is required',
        variant: 'destructive',
      });
      return;
    }
    setTests(tests.filter((_, i) => i !== index));
  };

  const handleTestChange = (index: number, field: keyof LabTest, value: any) => {
    const updated = [...tests];
    updated[index] = { ...updated[index], [field]: value };
    setTests(updated);
  };

  const handleSave = async () => {
    // Validate
    if (!provider) {
      toast({
        title: 'Validation Error',
        description: 'Lab provider is required',
        variant: 'destructive',
      });
      return;
    }

    if (!collectionDate) {
      toast({
        title: 'Validation Error',
        description: 'Collection date is required',
        variant: 'destructive',
      });
      return;
    }

    const validTests = tests.filter(t => t.test_name && t.value !== null);
    if (validTests.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'At least one test with a name and value is required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      
      // Set metadata for all tests
      const testsWithMetadata = validTests.map(test => ({
        ...test,
        lab_name: provider,
        performed_at: collectionDate,
      }));

      if (onSave) {
        await onSave(testsWithMetadata, { provider, collectionDate });
      }

      toast({
        title: 'Success',
        description: 'Lab results saved successfully',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save lab results',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Metadata */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lab Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="provider">Lab Provider *</Label>
            <Input
              id="provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder="e.g., Quest Diagnostics, LabCorp"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="collectionDate">Collection Date *</Label>
            <Input
              id="collectionDate"
              type="date"
              value={collectionDate}
              onChange={(e) => setCollectionDate(e.target.value)}
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Tests */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Test Results</CardTitle>
          <Button onClick={handleAddTest} variant="outline" size="sm" className="gap-2">
            <Plus className="h-4 w-4" />
            Add Test
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {tests.map((test, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-4 relative">
              {tests.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveTest(index)}
                  className="absolute top-2 right-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor={`test-name-${index}`}>Test Name *</Label>
                  <Input
                    id={`test-name-${index}`}
                    value={test.test_name}
                    onChange={(e) => handleTestChange(index, 'test_name', e.target.value)}
                    placeholder="e.g., Testosterone, Total"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor={`test-value-${index}`}>Value *</Label>
                  <Input
                    id={`test-value-${index}`}
                    type="number"
                    step="any"
                    value={test.value || ''}
                    onChange={(e) => handleTestChange(index, 'value', parseFloat(e.target.value) || null)}
                    placeholder="e.g., 450"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor={`test-unit-${index}`}>Unit *</Label>
                  <Input
                    id={`test-unit-${index}`}
                    value={test.unit}
                    onChange={(e) => handleTestChange(index, 'unit', e.target.value)}
                    placeholder="e.g., ng/dL"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor={`test-range-${index}`}>Reference Range</Label>
                  <Input
                    id={`test-range-${index}`}
                    value={test.reference_range || ''}
                    onChange={(e) => handleTestChange(index, 'reference_range', e.target.value)}
                    placeholder="e.g., 300-1000"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor={`test-status-${index}`}>Status</Label>
                  <Select
                    value={test.status || 'normal'}
                    onValueChange={(value) => handleTestChange(index, 'status', value)}
                  >
                    <SelectTrigger id={`test-status-${index}`} className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="elevated">Elevated</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="abnormal">Abnormal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button onClick={onCancel} variant="outline" disabled={saving}>
            Cancel
          </Button>
        )}
        <Button onClick={handleSave} disabled={saving} className="gap-2 bg-teal-600 hover:bg-teal-700">
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Lab Results'}
        </Button>
      </div>
    </div>
  );
};

export default ManualLabEntryForm;
